import { EMPTY_VALUE } from "./athleteUtils";

// The copy the V2 profile writes from the profile model: the header
// subtitle, the season, visibility and brand alignment reads, and the titles
// that change with the reader. Pure functions, shared by the desktop
// sections (ProfileBody) and the phone tabs (mobile/ProfileTabsMobile), so
// both trees say the same thing about the same athlete. The athlete's own
// page (selfView) speaks in the second person; coaches, reps, brands and
// staff read the third person, by first name where there is one.
//
// Each read keeps to its subject (Kobi's 10/2 note): the season reads to
// the player's game, the visibility read to the audience and what the
// athlete does off the court, the brand alignment read to the athlete's
// interests, NIL deals and work experience. A stored AI summary wins where
// there is one; the code-built reads fill in until it is regenerated.

// The engagement meter's marks, in this one place: the position average and
// the top 10% line. The desktop EngagementBenchmark and the phone
// EngagementCard draw them (utils/benchmarkScale) and the visibility read
// below quotes them.
export const ENGAGEMENT_AVG = 1.8;
export const ENGAGEMENT_TOP = 5;
export const ENGAGEMENT_MARKS = {
  low: ENGAGEMENT_AVG,
  high: ENGAGEMENT_TOP,
  lowLabel: "Avg",
  highLabel: "Top 10%",
  lowName: "position average",
  highName: "top 10% mark",
};

// A rate as the meter prints it: one decimal, none for a whole number on a
// mark ("1.8%", "5%"), always one for the athlete's own ("5.0%").
export const engagementText = (rate, { mark = false } = {}) => {
  if (typeof rate !== "number" || !Number.isFinite(rate)) return EMPTY_VALUE;
  return mark ? `${Number(rate.toFixed(1))}%` : `${rate.toFixed(1)}%`;
};

// Box score stats a season summary ranks the athlete on; minutes, turnovers
// and usage describe a role rather than a strength.
const RANKED = ["PPG", "APG", "RPG", "SPG", "FG%", "3P%", "FT%", "True shooting", "Effective FG%"];
const PER_GAME = [
  ["PPG", "point"],
  ["APG", "assist"],
  ["RPG", "rebound"],
  ["SPG", "steal"],
];
const SPLITS = [
  ["FG%", "from the field"],
  ["3P%", "from three"],
  ["FT%", "at the line"],
];

const fmt1 = (v) => (v == null ? EMPTY_VALUE : Number(v).toFixed(1));
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const textOf = (v) => String(v || "").trim();

// "A", "A and B", "A, B and C"; past `max` the rest are counted ("A, B and
// 2 more").
const listOf = (items, max = items.length) => {
  const shown = items.slice(0, max);
  if (items.length > shown.length) return `${shown.join(", ")} and ${items.length - shown.length} more`;
  return shown.length > 1 ? `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}` : shown[0] || "";
};

// The names in a profile list: interests and enrichments may be plain
// strings, the other lists {name, level}. Blank entries drop out.
const namesOf = (list) =>
  (Array.isArray(list) ? list : []).map((it) => textOf(typeof it === "string" ? it : it?.name)).filter(Boolean);

// An NIL deal by brand and deal type, never its value.
const dealOf = (deal) => {
  const brand = textOf(deal?.title);
  const type = textOf(deal?.description);
  return brand && type ? `${brand} (${type})` : brand;
};

// Work experience by company, role and months, never its pay. The
// spreadsheet import repeats the company as the role; that role drops out.
const jobOf = (job) => {
  const company = textOf(job?.companyName);
  const role = textOf(job?.role);
  const months = parseInt(job?.monthsWorked, 10);
  const details = [role.toLowerCase() !== company.toLowerCase() && role, months > 0 && plural(months, "month")].filter(Boolean);
  return company && details.length ? `${company} (${details.join(", ")})` : company;
};

// Who each sentence of a read is about: "You" on the athlete's own page; a
// viewer reads the first name once, then "They". `named` picks the verb
// ("Lleyton is", "You're", "They're").
const subjectsFor = (self, first) => {
  let opened = false;
  return () => {
    if (self) return { who: "You", named: false };
    if (opened) return { who: "They", named: false };
    opened = true;
    return { who: first, named: true };
  };
};

// The season summary from the box score, for when no AI summary is stored:
// per-game production over the games played (in the peer group's
// competition season when the box score shows that line, in a play group's
// season when it shows that), the best two ranks in the top half against
// the peer group, then the shooting splits: the numbers the tiles show.
// `line` is the box score line shown (the model itself, or one of its
// playGroups). Only the player's game; "" when there is nothing to read.
const boxScoreRead = (line, self, first, peerLabel) => {
  const stats = [...(line.boxScore || []), ...(line.shooting || [])];
  const valueOf = (label) => stats.find((s) => s.label === label)?.value ?? null;
  const next = subjectsFor(self, first);
  const sentences = [];

  const perGame = PER_GAME.map(([label, noun]) => {
    const v = valueOf(label);
    return v == null ? null : `${v} ${noun}${v === "1" ? "" : "s"}`;
  }).filter(Boolean);
  if (perGame.length) {
    const { who, named } = next();
    const games = line.gamesPlayed > 0 ? ` over ${plural(line.gamesPlayed, "game")}` : "";
    const where = line.lineName ? ` in ${line.lineName}` : "";
    sentences.push(`${who}${named ? " is" : "'re"} averaging ${listOf(perGame)}${games}${where}.`);
  }

  const ranks = stats
    .filter((s) => RANKED.includes(s.label) && s.nationalTop != null && s.nationalTop <= 50)
    .sort((a, b) => a.nationalTop - b.nationalTop)
    .slice(0, 2)
    // "True shooting" reads in lower case mid-sentence; "PPG" stays.
    .map((s) => `top ${s.nationalTop}% in ${s.label.replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase())}`);
  if (ranks.length) {
    const read = listOf(ranks);
    sentences.push(`${read.charAt(0).toUpperCase()}${read.slice(1)}${peerLabel ? ` vs ${peerLabel}` : ""}.`);
  }

  const splits = SPLITS.map(([label, where]) => {
    const v = valueOf(label);
    return v == null ? null : `${v} ${where}`;
  }).filter(Boolean);
  const trueShooting = valueOf("True shooting");
  if (splits.length) {
    const { who, named } = next();
    sentences.push(`${who}${named ? " is" : "'re"} shooting ${listOf(splits)}${trueShooting ? `, with ${trueShooting} true shooting` : ""}.`);
  } else if (trueShooting) {
    sentences.push(`True shooting sits at ${trueShooting}.`);
  }
  return sentences.join(" ");
};

// Header subtitle: sport · position · school · hometown · class. The school
// is the model's headline: the current college or high school (a pro's
// University), else the club or team, never a college athlete's last high
// school. The graduation year is the high school one, so past high school
// it reads "HS class of"; a college athlete's college level (JR) comes
// first. The year is the model's (the athlete's own entry first, Cerebro's
// only filling a blank), so the header and the About always show the same
// one. The level is the one the profile reads.
export const profileSubtitle = (model, athlete = {}, cacheInformation = {}) => {
  const gradYear = model.gradYear;
  const pastHighSchool = (model.levelOfPlay ?? Number(athlete.levelOfPlay)) >= 2;
  const year = model.collegeLevel || (gradYear ? `${pastHighSchool ? "HS class" : "Class"} of ${gradYear}` : null);
  const chipValue = (...prefixes) => model.metaChips.find((c) => prefixes.some((p) => c.startsWith(`${p}:`)))?.split(": ")[1];
  const place = model.headline ?? chipValue("College", "University", "High school", "Club", "Team");
  return [model.sport, model.positionName, place, chipValue("Hometown"), year]
    .filter(Boolean)
    .join(" · ");
};

// Everything the profile sections derive from the model before drawing.
// `matches` is null while brand matching runs, [] for none.
export const profileNarratives = ({ model, athlete = {}, cacheInformation = {}, matches, selfView = false }) => {
  const self = selfView;
  const first = model.firstName || "This athlete";
  const firstPossessive = model.firstName ? `${model.firstName}'s` : "This athlete's";

  const linkedRates = model.platforms.filter((p) => p.linked && p.engagement != null).map((p) => p.engagement);
  const engagement = model.engagement ? model.engagement : linkedRates.length ? linkedRates.reduce((a, b) => a + b, 0) / linkedRates.length : null;
  const linkedCount = model.platforms.filter((p) => p.linked).length;

  const cs = model.currentSeason;
  // The Current Season card for one season (the year picker's, else the
  // season shown first): its subtitle and bars, the peer comparison and the
  // teammates when they cover it, and a note naming the season they cover
  // when they do not. A roster from a season not on file (its
  // `rosterSeason`) says which season it covers and where it shows.
  const seasonFor = (label) => {
    if (!cs) return null;
    const season = cs.seasons.find((s) => s.label === label) || cs.seasons.find((s) => s.label === cs.defaultSeason) || cs.seasons[0];
    const comparison = cs.comparison && cs.comparison.season === season.label ? cs.comparison : null;
    const teammates = cs.teammates && cs.teammates.season === season.label ? cs.teammates : null;
    const peersElsewhere = cs.comparison && !comparison ? cs.comparison.season : null;
    const teamElsewhere = cs.teammates && !teammates ? cs.teammates : null;
    const teamNote = !teamElsewhere
      ? ""
      : teamElsewhere.rosterSeason
        ? `The teammate comparison covers the ${teamElsewhere.rosterSeason} and shows under ${teamElsewhere.season}.`
        : `The teammate comparison covers the ${teamElsewhere.season} season.`;
    const note =
      peersElsewhere && teamElsewhere && !teamElsewhere.rosterSeason && peersElsewhere === teamElsewhere.season
        ? `Comparisons cover the ${peersElsewhere} season.`
        : [peersElsewhere && `The peer comparison covers the ${peersElsewhere} season.`, teamNote].filter(Boolean).join(" ");
    const by = { games: ", game by game", events: ", event by event" }[season.kind] || "";
    return {
      season,
      subtitle: `${self ? "Your" : firstPossessive} ${season.label} season${by}`,
      series: season.points.map((p) => ({ ...p, avg: comparison ? comparison.avgPpg : null })),
      comparison,
      teammates,
      note,
    };
  };
  // The outlook under the card, on the season shown first: the athlete
  // against the group's average when the comparison covers that season,
  // else that season's scoring. Only the player's game.
  const seasonAi = (() => {
    if (!cs) return "";
    const { season, comparison: c } = seasonFor(cs.defaultSeason);
    if (c && c.ppg != null && c.avgPpg != null) {
      const average = c.avgName.toLowerCase().replace(/\bavg\b/, "average");
      const diff = Math.round((c.ppg - c.avgPpg) * 10) / 10;
      const lead = self ? "You're" : `${first} is`;
      const read =
        diff === 0
          ? `${lead} scoring right at the ${average}.`
          : `${lead} ${diff > 0 ? "outscoring" : "trailing"} the ${average} by ${fmt1(Math.abs(diff))} PPG.`;
      const apgClause = c.apg != null && c.avgApg > 0 ? ` ${self ? "Your" : "The"} ${fmt1(c.apg)} APG runs ${fmt1(c.apg / c.avgApg)}× the ${average}.` : "";
      return `${read}${apgClause}`;
    }
    if (season.ppg == null) return "";
    const games = season.games > 0 ? ` over ${plural(season.games, "game")}` : "";
    return `${self ? "You" : first} averaged ${fmt1(season.ppg)} PPG${games} in ${season.label}.`;
  })();

  const tierLabel = model.nilxTier ? model.nilxTier.charAt(0).toUpperCase() + model.nilxTier.slice(1) : null;
  const hasMatches = Array.isArray(matches) && matches.length > 0;
  const matching = matches === null;
  // Brand alignment: what the athlete has put on the profile, never the
  // visibility read and never the matches' activation ideas (Atlas shows
  // those).
  const brandNarrative = (() => {
    const interests = namesOf(athlete.interests);
    const deals = (Array.isArray(athlete.nilDeals) ? athlete.nilDeals : []).map(dealOf).filter(Boolean);
    const jobs = (Array.isArray(athlete.internships) ? athlete.internships : []).map(jobOf).filter(Boolean);
    const facts = [
      interests.length > 0 &&
        (({ who, named }) => `${who} ${named ? "lists" : "list"} ${listOf(interests, 4)} as ${interests.length === 1 ? "an interest" : "interests"}.`),
      deals.length > 0 &&
        (({ who, named }) => `${who}${named ? " has" : "'ve"} signed ${deals.length === 1 ? "an NIL deal" : "NIL deals"} with ${listOf(deals, 3)}.`),
      jobs.length > 0 && (({ who, named }) => `${who}${named ? " has" : "'ve"} worked at ${listOf(jobs, 2)}.`),
    ].filter(Boolean);
    if (facts.length) {
      const next = subjectsFor(self, first);
      return facts.map((say) => say(next())).join(" ");
    }
    return self
      ? "Add your interests, NIL deals and work experience under Edit my profile. Brand fit is read from them."
      : `${first} has not added interests, NIL deals or work experience yet.`;
  })();
  const brandSubtitle = hasMatches
    ? `Synergy-matched sponsors from ${self ? "your" : "the athlete's"} audience, values, and story, not commitments`
    : `Based on ${self ? "your" : "the athlete's"} audience profile, categories and tiers, not commitments`;

  // Visibility: the engagement rate against the meter's marks (a strong
  // signal only above the average), the lever while a platform is left to
  // link, then what the athlete does off the court (never the GPA).
  const visibilityRead = (() => {
    if (!model.hasVisibility) {
      return self
        ? "Add your Instagram, TikTok, X or YouTube under Edit my profile. Linked platforms feed your visibility score and your CacheNILx value."
        : `${first} has not linked Instagram, TikTok, X or YouTube yet. Linked platforms feed the visibility score and the CacheNILx value.`;
    }
    const whose = self ? "Your" : firstPossessive;
    const platformsClause = linkedCount ? ` across ${plural(linkedCount, "linked platform")}` : "";
    const rate = engagement != null ? Number(engagement.toFixed(1)) : null;
    let read;
    if (rate == null) {
      read =
        linkedCount === 1
          ? `${whose} linked platform has no engagement rate measured yet.`
          : `${whose} ${plural(linkedCount, "linked platform")} have no engagement rate measured yet.`;
    } else if (rate > ENGAGEMENT_AVG) {
      const mark = rate >= ENGAGEMENT_TOP ? `It clears ${ENGAGEMENT_TOP}%, the top 10% mark for the position.` : `It beats the ${ENGAGEMENT_AVG}% position average.`;
      read = `${whose} ${rate.toFixed(1)}% engagement rate is a strong signal at ${self ? "your" : "this"} reach${platformsClause}. ${mark}`;
    } else {
      read = `${whose} engagement rate is ${rate.toFixed(1)}%${platformsClause}, against a ${ENGAGEMENT_AVG}% position average.`;
    }
    const lever =
      linkedCount < model.platforms.length
        ? self
          ? `Linking ${linkedCount ? "another" : "a"} platform is the fastest lever to lift your Visibility pillar and, with it, your overall CacheScore.`
          : `${linkedCount ? "Another" : "A"} linked platform is the fastest lever on the Visibility pillar.`
        : "";
    return [read, lever].filter(Boolean).join(" ");
  })();
  const offCourt = [...namesOf(athlete.activities), ...namesOf(athlete.academicAwards), ...namesOf(athlete.enrichments)];
  const offCourtRead = offCourt.length
    ? `Off the court, ${self ? "your" : model.firstName ? `${model.firstName}'s` : "this athlete's"} profile lists ${listOf(offCourt, 3)}.`
    : "";
  const visibilityNarrative = model.engagementSummary || [visibilityRead, offCourtRead].filter(Boolean).join(" ");
  const visibilityTitle = self
    ? model.hasVisibility
      ? "Growing faster"
      : "Link your socials"
    : model.hasVisibility
      ? "Visibility read"
      : "No linked socials";

  // The box score's lines (D7): the model's playGroups when the payload
  // sorts its play into school, grassroots, college or pro, else the model
  // itself. `boxLine(key)` is the line a group switch shows (the default
  // group's for an unknown key).
  const lines = Array.isArray(model.playGroups) ? model.playGroups : [];
  const boxLine = (key) => lines.find((g) => g.key === key) || lines.find((g) => g.key === model.defaultGroup) || lines[0] || model;
  const shownLine = boxLine(null);

  const showStats = model.hasBoxScore || lines.some((g) => g.hasBoxScore) || !!cs;
  // The subtitle names the line the tiles show. The profile's own line:
  // what its ranks compare with (the peer group, no percentile wording
  // without one), then the group's competition season and its games, else
  // the games of the scoring aggregate, and its play group when there is
  // only one ("· Grassroots"). A play group's season total: its
  // competitions, teams, season and games ("Coastal League · Harbor Prep
  // School · 2026-27 · 4 games").
  const boxScoreSubtitleFor = (line = model) =>
    [
      // Stats from a team the athlete has left (a stand-in until their
      // team's feed has theirs) say so first.
      model.statsStandIn || null,
      ...(line.subtitleName
        ? [line.subtitleName]
        : [
          line.hasPercentiles ? `Key stats, percentile vs ${model.peerLabel}` : self ? "Key stats from your latest season" : "Key stats from the latest season",
          line.boxScoreLine || (line.gamesPlayed != null ? `${plural(line.gamesPlayed, "game")} played` : null),
          line.tag || null,
        ]),
    ]
      .filter(Boolean)
      .join(" · ");
  // The season summary under the tiles: the stored AI summary is written
  // from the scoring aggregate, so it shows only while the tiles show that
  // aggregate; beside a group's line (the peer group's, a play group's
  // season total) the read of the tiles stands in. "" draws no box.
  const performanceSummaryFor = (line = model) =>
    (!line.showsGroupLine && model.performanceSummary) || boxScoreRead(line, self, first, model.peerLabel);

  return {
    self,
    first,
    firstPossessive,
    engagement,
    linkedCount,
    cs,
    seasonFor,
    seasonAi,
    tierLabel,
    hasMatches,
    matching,
    brandNarrative,
    brandSubtitle,
    visibilityNarrative,
    visibilityTitle,
    showStats,
    // The line the box score opens on, and how a group switch reads another.
    boxLine,
    boxScoreSubtitleFor,
    performanceSummaryFor,
    boxScoreSubtitle: boxScoreSubtitleFor(shownLine),
    // "" draws no season summary box (performanceSummaryFor).
    performanceSummary: performanceSummaryFor(shownLine),
  };
};
